import React, { useMemo, useState } from "react";
import { Edit3, Plus, RefreshCcw, Trash2 } from "lucide-react";
import { useAdminPortal } from "../AdminPortalContext";
import {
  createAdminBlog,
  deleteAdminBlog,
  updateAdminBlog,
} from "../../../../../utils/blogApi";

const initialFormState = {
  title: "",
  excerpt: "",
  content: "",
  category: "",
  authorName: "BusiGrow Team",
  readTimeMinutes: 5,
  tags: "",
  coverImageUrl: "",
  isFeatured: false,
  isHero: false,
  isPublished: false,
};

const AdminBlogsPage = () => {
  const { blogs, fetchBlogs, setErrorMessage, theme, token } = useAdminPortal();
  const isDarkTheme = theme === "dark";

  const [form, setForm] = useState(initialFormState);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sortedBlogs = useMemo(() => {
    return [...blogs].sort((left, right) => {
      const leftDate = new Date(left.publishedAt || left.createdAt).getTime();
      const rightDate = new Date(
        right.publishedAt || right.createdAt,
      ).getTime();
      return rightDate - leftDate;
    });
  }, [blogs]);

  const resetForm = () => {
    setForm(initialFormState);
    setSelectedFile(null);
    setEditingBlogId(null);
  };

  const buildFormData = () => {
    const payload = new FormData();

    Object.entries(form).forEach(([key, value]) => {
      payload.append(key, String(value));
    });

    if (selectedFile) {
      payload.append("coverImage", selectedFile);
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const payload = buildFormData();

      if (editingBlogId) {
        await updateAdminBlog(token, editingBlogId, payload);
      } else {
        await createAdminBlog(token, payload);
      }

      await fetchBlogs(token);
      resetForm();
    } catch (error) {
      setErrorMessage(error.message || "Failed to save blog");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (blog) => {
    setEditingBlogId(blog.id);
    setSelectedFile(null);
    setForm({
      title: blog.title,
      excerpt: blog.excerpt,
      content: blog.content,
      category: blog.category,
      authorName: blog.authorName,
      readTimeMinutes: blog.readTimeMinutes || 5,
      tags: Array.isArray(blog.tags) ? blog.tags.join(", ") : "",
      coverImageUrl: blog.coverImageUrl || "",
      isFeatured: Boolean(blog.featured),
      isHero: Boolean(blog.isHero),
      isPublished: Boolean(blog.isPublished),
    });
  };

  const handleDelete = async (blogId) => {
    const confirmed = window.confirm("Delete this blog permanently?");
    if (!confirmed) {
      return;
    }

    setErrorMessage("");

    try {
      await deleteAdminBlog(token, blogId);
      await fetchBlogs(token);
      if (editingBlogId === blogId) {
        resetForm();
      }
    } catch (error) {
      setErrorMessage(error.message || "Failed to delete blog");
    }
  };

  const handlePublishToggle = async (blog) => {
    setErrorMessage("");

    try {
      const payload = new FormData();
      payload.append("isPublished", String(!blog.isPublished));
      await updateAdminBlog(token, blog.id, payload);
      await fetchBlogs(token);
    } catch (error) {
      setErrorMessage(error.message || "Failed to update publish state");
    }
  };

  return (
    <div className="space-y-6">
      <section
        className={`rounded-[28px] border px-6 py-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-2xl font-bold">BusiMag Blog Manager</h2>
            <p
              className={`mt-2 text-sm leading-6 ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
            >
              Create, edit, publish, and remove blog posts. Published posts are
              rendered on the landing page and BusiMag automatically. The
              BusiMag hero image card is driven by the latest published featured
              post cover image and title.
            </p>
          </div>

          <button
            type="button"
            onClick={() => fetchBlogs(token)}
            className={`inline-flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold transition ${isDarkTheme ? "bg-slate-800 text-slate-100 hover:bg-slate-700" : "bg-slate-900 text-white hover:bg-slate-800"}`}
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <article
          className={`rounded-[28px] border p-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
        >
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-bold">
              {editingBlogId ? "Edit Blog" : "Create Blog"}
            </h3>
            {editingBlogId && (
              <button
                type="button"
                onClick={resetForm}
                className="text-xs font-semibold text-sky-600 hover:text-sky-500"
              >
                Clear form
              </button>
            )}
          </div>

          <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="space-y-2">
                <span className="text-sm font-semibold">Title</span>
                <input
                  required
                  value={form.title}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      title: event.target.value,
                    }))
                  }
                  className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                  placeholder="How AI can improve retention"
                />
              </label>

              <label className="space-y-2">
                <span className="text-sm font-semibold">Category</span>
                <input
                  required
                  value={form.category}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      category: event.target.value,
                    }))
                  }
                  className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                  placeholder="AI & Tech"
                />
              </label>
            </div>

            <label className="space-y-2 block">
              <span className="text-sm font-semibold">Excerpt</span>
              <textarea
                required
                rows={3}
                value={form.excerpt}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    excerpt: event.target.value,
                  }))
                }
                className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                placeholder="Brief summary shown in cards"
              />
            </label>

            <label className="space-y-2 block">
              <span className="text-sm font-semibold">Content</span>
              <textarea
                required
                rows={8}
                value={form.content}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    content: event.target.value,
                  }))
                }
                className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                placeholder="Full article content"
              />
            </label>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="space-y-2">
                <span className="text-sm font-semibold">Author Name</span>
                <input
                  value={form.authorName}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      authorName: event.target.value,
                    }))
                  }
                  className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                />
              </label>

              <label className="space-y-2">
                <span className="text-sm font-semibold">
                  Read Time (minutes)
                </span>
                <input
                  type="number"
                  min={1}
                  max={120}
                  value={form.readTimeMinutes}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      readTimeMinutes: Number(event.target.value) || 1,
                    }))
                  }
                  className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="space-y-2">
                <span className="text-sm font-semibold">
                  Tags (comma-separated)
                </span>
                <input
                  value={form.tags}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      tags: event.target.value,
                    }))
                  }
                  className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                  placeholder="AI, SaaS, Growth"
                />
              </label>

              <label className="space-y-2">
                <span className="text-sm font-semibold">
                  Cover Image URL (optional)
                </span>
                <input
                  value={form.coverImageUrl}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      coverImageUrl: event.target.value,
                    }))
                  }
                  className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                  placeholder="https://..."
                />
              </label>
            </div>

            <label className="space-y-2 block">
              <span className="text-sm font-semibold">Upload Cover Image</span>
              <input
                type="file"
                accept="image/*"
                onChange={(event) =>
                  setSelectedFile(event.target.files?.[0] || null)
                }
                className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100 file:text-slate-300" : "border-slate-200 bg-slate-50 text-slate-900"}`}
              />
              <p
                className={`text-xs ${isDarkTheme ? "text-slate-500" : "text-slate-400"}`}
              >
                If selected, uploaded file takes priority over URL.
              </p>
            </label>

            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isFeatured}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      isFeatured: event.target.checked,
                    }))
                  }
                />
                Featured
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isHero}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      isHero: event.target.checked,
                    }))
                  }
                />
                Use as Hero
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      isPublished: event.target.checked,
                    }))
                  }
                />
                Publish now
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold transition ${isDarkTheme ? "bg-sky-500 text-slate-950 hover:bg-sky-400" : "bg-slate-900 text-white hover:bg-slate-800"} ${isSubmitting ? "opacity-70" : ""}`}
            >
              <Plus className="h-4 w-4" />
              {isSubmitting
                ? "Saving..."
                : editingBlogId
                  ? "Update Blog"
                  : "Create Blog"}
            </button>
          </form>
        </article>

        <article
          className={`rounded-[28px] border p-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
        >
          <h3 className="text-lg font-bold">
            Existing Blogs ({sortedBlogs.length})
          </h3>

          <div className="mt-5 space-y-3 max-h-[720px] overflow-auto pr-1">
            {sortedBlogs.length === 0 && (
              <div
                className={`text-sm ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
              >
                No blogs yet.
              </div>
            )}

            {sortedBlogs.map((blog) => (
              <div
                key={blog.id}
                className={`rounded-2xl border p-4 ${isDarkTheme ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold line-clamp-2">
                      {blog.title}
                    </p>
                    <p
                      className={`mt-1 text-xs ${isDarkTheme ? "text-slate-500" : "text-slate-400"}`}
                    >
                      {blog.category} • {blog.readTime} • {blog.date}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${blog.isPublished ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                      >
                        {blog.isPublished ? "Published" : "Draft"}
                      </span>
                      {blog.featured && (
                        <span className="rounded-full bg-violet-100 px-2.5 py-1 text-[11px] font-semibold text-violet-700">
                          Featured
                        </span>
                      )}
                      {blog.isHero && (
                        <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-semibold text-sky-700">
                          Hero
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(blog)}
                    className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition ${isDarkTheme ? "bg-slate-800 text-slate-100 hover:bg-slate-700" : "bg-white text-slate-700 hover:bg-slate-100"}`}
                  >
                    <Edit3 className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePublishToggle(blog)}
                    className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${blog.isPublished ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}
                  >
                    {blog.isPublished ? "Unpublish" : "Publish"}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(blog.id)}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-rose-100 px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-200 transition"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
};

export default AdminBlogsPage;
